import { HStack, Stack, Text } from "@chakra-ui/react";
import * as React from "react";

import {
  KeywordAgentTerm,
  excludeKeywordAgentTerm,
  restoreKeywordAgentTerm,
} from "../api/keyword-agent";
import { WrapLoading, useLoading } from "../hooks/loading";
import { useToaster } from "../hooks/toaster";
import { brand } from "../lib/brand";
import { Button } from "./button";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "./dialog";
import { NoteField } from "./note-field";
import { toaster } from "./toaster";

interface ReviewSearchTermDialogProps {
  isOpen: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  onSuccess: () => void;
  workspaceSlug: string;
  customerId: string;
  loginCustomerId: string | null;
  campaignId: number;
  adGroupId: number;
  terms: KeywordAgentTerm[];
}

// Both verdicts take an answer: cutting a term is a ruling like keeping
// one, and the answer is all the agent has to learn from either way.
export function ReviewSearchTermDialog({
  adGroupId,
  campaignId,
  customerId,
  isOpen,
  loginCustomerId,
  onOpenChange,
  onSuccess,
  terms,
  workspaceSlug,
}: ReviewSearchTermDialogProps) {
  const [index, setIndex] = React.useState(0);
  const [note, setNote] = React.useState("");
  // One apiece, so the button that was pressed is the one that spins.
  const [isKeeping, withKeeping] = useLoading();
  const [isExcluding, withExcluding] = useLoading();
  const isWorking = isKeeping || isExcluding;
  const withToaster = useToaster();

  const term = terms[index];
  const answer = note.trim();

  const handleOpenChange = (details: { open: boolean }) => {
    if (!details.open) {
      setIndex(0);
      setNote("");
    }
    onOpenChange(details);
  };

  const advance = () => {
    setNote("");
    if (index + 1 >= terms.length) {
      toaster.create({
        type: "success",
        title: `Got it. ${brand.productName} won't ask about these again.`,
      });
      handleOpenChange({ open: false });
      onSuccess();
    } else {
      setIndex(index + 1);
    }
  };

  // The two verdicts differ only in which endpoint the answer is posted
  // to, so they take the same arguments and are called the same way.
  const rule = (
    verdict: typeof excludeKeywordAgentTerm,
    withWorking: WrapLoading<unknown>,
  ) => {
    return async () => {
      if (!term || !answer) return;
      await withWorking(async () => {
        await withToaster(async () => {
          await verdict(
            workspaceSlug,
            customerId,
            loginCustomerId,
            campaignId,
            adGroupId,
            term.id,
            answer,
          );
          advance();
        });
      });
    };
  };

  return (
    <DialogRoot open={isOpen && !!term} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogCloseTrigger />
        <DialogHeader>
          <Stack gap="1">
            <Text color="fg.muted" fontSize="xs">
              {index + 1} of {terms.length}
            </Text>
            {/* A term judged before there was a question to ask falls
                back to the one the queue is there to settle. */}
            <DialogTitle>
              {term?.question ??
                `Does “${term?.term}” belong in this ad group?`}
            </DialogTitle>
          </Stack>
        </DialogHeader>
        <DialogBody>
          <Stack gap="3">
            {/* The title is a question about the business, so the term
                itself has to be on screen under it. */}
            <Text color="fg.muted" fontSize="sm">
              &ldquo;{term?.term}&rdquo;
            </Text>
            <Stack gap="1">
              <Text color="fg.muted" fontSize="sm">
                What {brand.productName} thinks
              </Text>
              <Text fontSize="sm">
                {term?.reason ?? "No rationale recorded."}
              </Text>
            </Stack>
            {term?.["fit-score"] !== null &&
              term?.["fit-score"] !== undefined && (
                <Text color="fg.muted" fontSize="sm">
                  {term["fit-score"]}/10 based on your criteria
                </Text>
              )}
            <NoteField
              // Remounted per term, so each question is asked with the
              // cursor already in the box.
              key={term?.id}
              label="Your answer"
              placeholder="Answer in a sentence"
              hint={`Required either way, this is how ${brand.productName} learns.`}
              value={note}
              onChange={setNote}
            />
          </Stack>
        </DialogBody>
        <DialogFooter>
          <HStack gap="2">
            <Button
              variant="surface"
              loading={isKeeping}
              disabled={!answer || isWorking}
              onClick={rule(restoreKeywordAgentTerm, withKeeping)}
            >
              It belongs
            </Button>
            <Button
              variant="solid"
              loading={isExcluding}
              disabled={!answer || isWorking}
              onClick={rule(excludeKeywordAgentTerm, withExcluding)}
            >
              Exclude it
            </Button>
          </HStack>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
